import type { Confidence, DocStatus, DocTypeId } from '../../types/document'
import type { Segment } from './text'

type Rule = [RegExp, number]

// Weighted keyword rules. A strong title phrase ("income certificate") scores 4;
// supporting vocabulary scores 1-2. Matches in the first lines score one extra.
const RULES: Record<Exclude<DocTypeId, 'application_decision' | 'unknown'>, Rule[]> = {
  income_certificate: [
    [/income certificate|आय प्रमाण\s*-?पत्र|उत्पन्नाचा दाखला|उत्पन्न प्रमाणपत्र|আয়ের শংসাপত্র|வருமான சான்றிதழ்|ఆదాయ ధృవీకరణ|આવકનો દાખલો|ಆದಾಯ ಪ್ರಮಾಣ|വരുമാന സർട്ടിഫിക്കറ്റ്|ਆਮਦਨ ਸਰਟੀਫਿਕੇਟ|ଆୟ ପ୍ରମାଣପତ୍ର/i, 4],
    [/annual income|family income|वार्षिक आय|सालाना आय|कुल आय|वार्षिक उत्पन्न/i, 2]
  ],
  caste_certificate: [
    [/caste certificate|community certificate|जाति प्रमाण\s*-?पत्र|जातीचा दाखला|জাতি শংসাপত্র|சாதி சான்றிதழ்|కుల ధృవీకరణ|જાતિ પ્રમાણપત્ર|ಜಾತಿ ಪ್ರಮಾಣ|ജാതി സർട്ടിഫിക്കറ്റ്|ਜਾਤੀ ਸਰਟੀਫਿਕੇਟ|ଜାତି ପ୍ରମାଣପତ୍ର/i, 4],
    [/scheduled caste|scheduled tribe|other backward|अनुसूचित जाति|अनुसूचित जनजाति|पिछड़ा वर्ग|\bobc\b/i, 2]
  ],
  domicile_certificate: [
    [/domicile|residence certificate|निवास प्रमाण\s*-?पत्र|मूल निवास|अधिवास|रहिवासी दाखला|বাসস্থান শংসাপত্র/i, 4]
  ],
  birth_certificate: [
    [/birth certificate|certificate of birth|जन्म प्रमाण\s*-?पत्र|जन्म दाखला|জন্ম শংসাপত্র|জন্ম প্রমাণপত্র|பிறப்புச் சான்றிதழ்|జనన ధృవీకరణ/i, 4],
    [/registration of births|जन्म पंजीकरण/i, 2]
  ],
  death_certificate: [
    [/death certificate|certificate of death|मृत्यु प्रमाण\s*-?पत्र|मृत्यू दाखला|মৃত্যু শংসাপত্র|இறப்புச் சான்றிதழ்/i, 4]
  ],
  electricity_bill: [
    [/electricity bill|बिजली बिल|बिजली का बिल|विद्युत बिल|वीज बिल|বিদ্যুৎ বিল|மின் கட்டண|విద్యుత్ బిల్లు|વીજળી બિલ/i, 4],
    [/electricity|बिजली|विद्युत|वीज|\bkwh\b|units? consumed|meter reading|मीटर रीडिंग|power (distribution|corporation)|discom|vidyut|energy charges/i, 2]
  ],
  water_bill: [
    [/water bill|पानी का बिल|जल बिल|पाणी बिल/i, 4],
    [/water (charges|supply|tax)|jal (board|sansthan|nigam)|जल (कर|संस्थान|बोर्ड)|पाणीपट्टी/i, 2]
  ],
  ration_card: [
    [/ration card|राशन कार्ड|शिधापत्रिका|रेशन कार्ड|রেশন কার্ড|குடும்ப அட்டை|రేషన్ కార్డు|રેશન કાર્ડ|ಪಡಿತರ ಚೀಟಿ|റേഷൻ കാർഡ്|ਰਾਸ਼ਨ ਕਾਰਡ|ରାସନ କାର୍ଡ/i, 4],
    [/fair price shop|उचित दर|\bpds\b|\bnfsa\b|अंत्योदय|antyodaya/i, 2]
  ],
  aadhaar: [
    // "Aadhaar card" alone is usually a required-document mention, not an Aadhaar letter
    [/uidai|unique identification authority|भारतीय विशिष्ट पहचान प्राधिकरण|aadhaar letter|e-aadhaar|आपका आधार|your aadhaar|enrol?ment no/i, 4],
    [/aadhaa?r|आधार\s*(कार्ड|संख्या|नंबर)/i, 1]
  ],
  pan_card: [
    [/permanent account number|pan card|स्थायी लेखा संख्या|पैन कार्ड/i, 4]
  ],
  bank_notice: [
    [/\bbank\b|बैंक|ব্যাংক|வங்கி|బ్యాంక్|બેંક|ಬ್ಯಾಂಕ್|ബാങ്ക്|ਬੈਂਕ|ବ୍ୟାଙ୍କ/i, 2],
    [/\bkyc\b|ifsc|savings account|बचत खाता|account (will be )?(frozen|blocked|inoperative)|branch manager|शाखा प्रबंधक/i, 2]
  ],
  loan_notice: [
    [/\bloan\b|ऋण|कर्ज|\bemi\b|overdue|\bnpa\b|recovery|वसूली|sarfaesi|demand notice|मांग सूचना/i, 2],
    [/outstanding|बकाया|थकबाकी/i, 1]
  ],
  income_tax_notice: [
    [/income[- ]tax|आयकर|प्राप्तिकर/i, 3],
    [/assessment year|निर्धारण वर्ष|\bitr\b|\bcpc\b|u\/s\s*1\d\d|section 1\d\d/i, 2]
  ],
  pension: [
    [/pension|पेंशन|निवृत्तिवेतन|পেনশন|ஓய்வூதியம்|పింఛను|પેન્શન|ಪಿಂಚಣಿ|പെൻഷൻ|ਪੈਨਸ਼ਨ|ପେନସନ/i, 3],
    [/old age|वृद्धावस्था|widow|विधवा|\bppo\b/i, 2]
  ],
  court_notice: [
    [/summons|समन|court|न्यायालय|अदालत|legal notice|कानूनी नोटिस|magistrate|मजिस्ट्रेट|warrant|वारंट|আদালত|நீதிமன்ற|న్యాయస్థాన|અદાલત|ನ್ಯಾಯಾಲಯ|കോടതി|ਅਦਾਲਤ|ଅଦାଲତ/i, 3],
    [/plaintiff|defendant|petitioner|respondent|वादी|प्रतिवादी|advocate|अधिवक्ता|वकील/i, 2]
  ],
  land_record: [
    [/khatauni|khasra|खतौनी|खसरा|jamabandi|जमाबंदी|7\/12|सातबारा|भू[- ]?अभिलेख|land record|record of rights|अधिकार अभिलेख|দাগ|খতিয়ান|பட்டா|పట్టా|ಪಹಣಿ/i, 4],
    [/mutation|दाखिल खारिज|नामांतरण|survey no|सर्वे नं|गाटा|plot no/i, 2]
  ],
  property_tax: [
    [/property tax|house tax|संपत्ति कर|गृह कर|भवन कर|मालमत्ता कर|holding tax|சொத்து வரி|ఆస్తి పన్ను/i, 4]
  ],
  scholarship: [
    [/scholarship|छात्रवृत्ति|शिष्यवृत्ती|বৃত্তি|உதவித்தொகை|ఉపకార వేతన|શિષ્યવૃત્તિ|ವಿದ್ಯಾರ್ಥಿ ವೇತನ|സ്കോളർഷിപ്പ്|ਵਜ਼ੀਫ਼ਾ|ଛାତ୍ରବୃତ୍ତି/i, 4]
  ],
  farmer_scheme: [
    [/pm[- ]?kisan|किसान सम्मान|kisan credit|किसान क्रेडिट|fasal bima|फसल बीमा|soil health/i, 4],
    [/farmer|kisan|किसान|कृषक|कृषि|शेतकरी|কৃষক|விவசாயி|రైతు|ખેડૂત|ರೈತ|കർഷക|ਕਿਸਾਨ|କୃଷକ/i, 2]
  ],
  school_letter: [
    [/school|विद्यालय|शाला|शाळा|admission|प्रवेश|principal|प्रधानाचार्य|मुख्याध्यापक|বিদ্যালয়|பள்ளி|పాఠశాల|શાળા|ಶಾಲೆ|സ്കൂൾ|ਸਕੂਲ|ବିଦ୍ୟାଳୟ/i, 2],
    [/parent|guardian|अभिभावक|पालक/i, 1]
  ],
  marksheet: [
    [/marksheet|mark sheet|statement of marks|अंकपत्र|अंक तालिका|अंक पत्र|गुणपत्रक|grade card|মার্কশিট/i, 4],
    [/total marks|पूर्णांक|प्राप्तांक|परिणाम|निकाल/i, 1]
  ],
  insurance: [
    [/insurance|बीमा|विमा|বিমা|காப்பீடு|బీమా|વીમો|ವಿಮೆ|ഇൻഷുറൻസ്|ਬੀਮਾ|ବୀମା/i, 3],
    [/premium|प्रीमियम|policy|पॉलिसी|ayushman|आयुष्मान|pmjay|pmsby|pmjjby/i, 2]
  ],
  traffic_challan: [
    [/e-?challan|traffic|यातायात|motor vehicles act|मोटर वाहन अधिनियम/i, 3],
    [/challan|चालान|vehicle no|वाहन (सं|संख्या|नं)|violation|उल्लंघन/i, 2]
  ],
  job_notice: [
    [/recruitment|भर्ती|vacanc|रिक्ति|रिक्त पद|posts? of|पदों|selection list|चयन सूची|merit list|waiting list|प्रतीक्षा सूची|interview|साक्षात्कार|walk-in|public service commission|लोक सेवा आयोग|staff selection|चयन आयोग|written (exam|test)|लिखित परीक्षा|admit card|नियुक्ति|appointment|भरती|নিয়োগ|ஆட்சேர்ப்பு|నియామక|ನೇಮಕಾತಿ|നിയമനം|ભરતી|ਭਰਤੀ|ନିଯୁକ୍ତି/i, 3],
    [/candidates?|अभ्यर्थी|उम्मीदवार|eligib|पात्रता|examination|परीक्षा|upsc|\bssc\b|\bcbse\b/i, 1]
  ],
  application_receipt: [
    [/acknowledge?ment|पावती|receipt of application|आवेदन प्राप्ति|registration slip/i, 3],
    [/application (has been |is )?(received|submitted|registered)|आवेदन (प्राप्त|जमा) (हो|किया)|application status/i, 3]
  ],
  lab_report: [
    [/pathology|laboratory|diagnostic\s*(centre|center|lab)|lab\s*report|test\s*report|biological\s*ref|reference\s*(range|interval)|haematology|hematology|biochemistry|lipid\s*profile|liver\s*function|kidney\s*function|thyroid\s*profile|complete\s*blood\s*count|\bcbc\b/i, 4],
    [/h(a)?emoglobin|platelet|creatinine|cholesterol|hba1c|\btsh\b|sgpt|bilirubin|blood\s*sugar|glucose|g\/dl|mg\/dl/i, 2]
  ],
  rental_agreement: [
    [/rent(al)?\s*agreement|lease\s*(deed|agreement)|leave\s*and\s*licen[cs]e|tenancy\s*agreement|किराया\s*(अनुबंध|समझौता|नामा)|किरायानामा|भाडे\s*करार|ভাড়া(র)?\s*চুক্তি|வாடகை\s*ஒப்பந்த|అద్దె\s*ఒప్పంద|ભાડા\s*કરાર|ಬಾಡಿಗೆ\s*ಒಪ್ಪಂದ|വാടക\s*കരാർ/i, 5],
    [/lessor|lessee|licensor|licensee|landlord|tenant|monthly\s*rent|security\s*deposit|मकान\s*मालिक|किरायेदार|घरमालक|भाडेकरू/i, 2]
  ],
  loan_agreement: [
    [/loan\s*agreement|sanction\s*letter|facility\s*agreement|key\s*fact\s*statement|\bkfs\b|ऋण\s*(अनुबंध|समझौता)|लोन\s*एग्रीमेंट|कर्ज\s*करार/i, 5],
    [/borrower|co-?borrower|rate\s*of\s*interest|annual\s*percentage\s*rate|\bapr\b|foreclosure|pre-?payment|processing\s*fee|emi|tenure|उधारकर्ता|ब्याज\s*दर/i, 2]
  ],
  contract: [
    [/\bagreement\b|\bcontract\b|memorandum\s*of\s*understanding|\bmou\b|अनुबंध|समझौता|करारनामा|इकरारनामा|চুক্তি|ஒப்பந்த|ఒప్పంద|કરાર|ಒಪ್ಪಂದ|കരാർ|ਇਕਰਾਰਨਾਮਾ|ଚୁକ୍ତି/i, 2],
    [/hereinafter|party\s*of\s*the\s*(first|second)\s*part|whereas|witnesseth|in\s*witness\s*whereof|terms\s*and\s*conditions|शर्तें|प्रथम\s*पक्ष|द्वितीय\s*पक्ष/i, 2]
  ],
  government_notice: [
    [/\bnotice\b|सूचना|नोटिस|circular|परिपत्र|office memorandum|ज्ञापन|अधिसूचना|notification/i, 1],
    [/government of|govt\.? of|सरकार|शासन/i, 1]
  ]
}

export interface Classification {
  type: DocTypeId
  confidence: Confidence
  evidence: string[]
}

export function classify(segments: Segment[], status: DocStatus | null): Classification {
  const fullText = segments.map((s) => s.text).join('\n')
  const topText = segments.slice(0, 6).map((s) => s.text).join('\n')

  const scores: Array<{ type: DocTypeId; score: number; evidence: string[] }> = []
  for (const [type, rules] of Object.entries(RULES) as Array<[DocTypeId, Rule[]]>) {
    let score = 0
    const evidence: string[] = []
    for (const [re, weight] of rules) {
      const m = fullText.match(re)
      if (!m) continue
      score += weight + (re.test(topText) ? 1 : 0)
      evidence.push(m[0])
    }
    if (score > 0) scores.push({ type, score, evidence })
  }

  // A clear approval/rejection/pending letter about an application
  if (status) {
    const subject = fullText.match(/application|आवेदन|अर्ज|আবেদন|request|claim|दावा/i)
    if (subject) scores.push({ type: 'application_decision', score: 4, evidence: [subject[0]] })
  }

  scores.sort((a, b) => b.score - a.score)
  const [best, second] = scores
  if (!best || best.score < 2) return { type: 'unknown', confidence: 'low', evidence: best?.evidence ?? [] }

  // A certificate/bill/etc. that also mentions "approved" is still that document
  if (best.type === 'application_decision' && second && second.score >= 4 && second.type !== 'government_notice') {
    return { type: second.type, confidence: 'medium', evidence: second.evidence }
  }

  // The generic "government notice" type is a fallback; it shouldn't make a
  // clear specific match (e.g. a Staff Selection Commission notice) look unsure
  const rival = scores.slice(1).find((c) => c.type !== 'government_notice')
  const margin = rival ? best.score / rival.score : Infinity
  let confidence: Confidence = best.score >= 4 && margin >= 1.5 ? 'high' : best.score >= 3 ? 'medium' : 'low'
  if (best.type === 'government_notice' && confidence === 'high') confidence = 'medium'
  return { type: best.type, confidence, evidence: best.evidence }
}
